import { motion } from 'framer-motion';
import { Check, X, Zap, RefreshCw, Minus, Star, Award, TrendingDown } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Capacitor } from '@capacitor/core';

import CreditPurchaseModal from '../components/CreditSystem/CreditPurchaseModal';
import { goToCreditCheckout } from '../utils/creditCheckoutNavigation';
import MarketingPageFooter from '../components/marketing/MarketingPageFooter';
import MarketingLandingNav from '../components/marketing/MarketingLandingNav';
import MarketingAgentBar, { MARKETING_AGENT_STICKY_ZONE_ID } from '../components/marketing/MarketingAgentBar';
import PricingFAQ, { pricingFaqJsonLd } from '../components/marketing/PricingFAQ';
import { supabase } from '../lib/supabase';
import { SEO } from '../shared/components/SEO';
import { buildBreadcrumbJsonLd } from '../shared/seo/breadcrumbJsonLd';
import { formatPackPrice } from '../utils/localePrice';
import { trackCTA } from '../utils/metaPixel';
import { getLocalizedPricingSeo } from '../content/i18n/marketingPageSeo';
import { usePricingPageContent, type FeatureValue } from './pricing/usePricingPageContent';

const PENDING_PLAN_KEY = 'pricing_pending_plan';

type PlanId = 'free' | 'monthly' | 'quarterly' | 'halfyearly';

function FeatureCell({ value, highlight = false }: { value: FeatureValue; highlight?: boolean }) {
  if (value === true)
    return <Check className={`w-4 h-4 shrink-0 ${highlight ? 'text-[var(--vscode-primary)]' : 'text-green-500'}`} />;
  if (value === false)
    return <X className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />;
  if (value === null)
    return <Minus className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 shrink-0" />;
  return (
    <span className={`text-xs font-medium whitespace-nowrap ${highlight ? 'text-[var(--vscode-primary)]' : 'text-[var(--vscode-text-secondary)]'}`}>
      {value}
    </span>
  );
}

interface PricingPageProps {
  onNavigate?: (view: string) => void;
}

const PricingPage: React.FC<PricingPageProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const { plans, planFeatures, competitors, compareRows, priceSavings, copy } = usePricingPageContent();
  const seo = getLocalizedPricingSeo(t);
  const [loading, setLoading] = React.useState<string | null>(null);
  const [purchaseModalOpen, setPurchaseModalOpen] = React.useState(false);
  const [checkoutPackId, setCheckoutPackId] = React.useState<string | undefined>();
  const locale = i18n.language || 'en';
  const GGJ_COL = 0;

  React.useEffect(() => {
    const pendingPlan = sessionStorage.getItem(PENDING_PLAN_KEY);
    if (!pendingPlan) return;
    const checkAndOpen = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        sessionStorage.removeItem(PENDING_PLAN_KEY);
        if (Capacitor.isNativePlatform()) {
          setCheckoutPackId(pendingPlan);
          setPurchaseModalOpen(true);
        } else {
          goToCreditCheckout(pendingPlan);
        }
      }
    };
    checkAndOpen();
  }, []);

  const handlePlanClick = async (plan: (typeof plans)[0]) => {
    trackCTA(`pricing_${plan.id}`);
    if (plan.id === 'free') {
      if (onNavigate) onNavigate('signup');
      else window.location.href = '/signup';
      return;
    }
    setLoading(plan.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        sessionStorage.setItem(PENDING_PLAN_KEY, plan.id);
        if (onNavigate) onNavigate('signup');
        else window.location.href = `/signup?plan=${plan.id}`;
        return;
      }
      const packId = plan.packId ?? plan.id;
      if (Capacitor.isNativePlatform()) {
        setCheckoutPackId(packId);
        setPurchaseModalOpen(true);
      } else {
        goToCreditCheckout(packId);
      }
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="pricing-marketing-page text-gray-900 dark:text-white transition-colors duration-300 overflow-x-hidden">
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="https://www.gograbjob.com/pricing"
        jsonLd={[
          buildBreadcrumbJsonLd([
            { name: t('common.home', { defaultValue: 'Home' }), path: '/' },
            { name: seo.breadcrumb },
          ]),
          pricingFaqJsonLd,
        ]}
      />

      <MarketingLandingNav onNavigate={onNavigate} activeView="pricing" variant="marketing" />

      <div
        id={MARKETING_AGENT_STICKY_ZONE_ID}
        className="landing-agent-sticky-zone marketing-agent-sticky-zone--below-nav"
      >
        <MarketingAgentBar onNavigate={onNavigate} />

      <main className="flex-1 pb-[calc(2rem+env(safe-area-inset-bottom))] sm:pb-[calc(4rem+env(safe-area-inset-bottom))] px-3 sm:px-6 lg:px-8 overflow-x-hidden" key={i18n.language}>
        <div className="max-w-7xl mx-auto w-full min-w-0">

          {/* ── Hero ──────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-10 sm:mb-12 pt-10 sm:pt-14 lg:pt-16"
          >
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 px-1">
              {copy.heroTitle}
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-6 px-1">
              {copy.heroSubtitle}
            </p>
            <div className="flex flex-wrap justify-center gap-x-4 sm:gap-x-6 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-green-500" /> {copy.oneMinuteSetup}</span>
              <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-green-500" /> {copy.freeToStart}</span>
              <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-green-500" /> {copy.cancelAnytime}</span>
              <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-green-500" /> {copy.indiaNativePricing}</span>
            </div>
          </motion.div>

          {/* ── Plan Cards ─────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto min-w-0 mb-16 sm:mb-20">
            {plans.map((plan, index) => {
              const featureRow = planFeatures.map((row) => ({
                label: row.label,
                value: row[plan.id] as FeatureValue,
              }));

              return (
                <motion.article
                  key={plan.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.12 + index * 0.08 }}
                  className={`relative rounded-xl sm:rounded-2xl flex flex-col border transition-colors min-w-0 overflow-hidden ${
                    plan.popular
                      ? 'border-[var(--vscode-primary)] shadow-lg shadow-[var(--vscode-primary)]/15 bg-[var(--vscode-bg-elevated)]'
                      : 'border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)] hover:border-[var(--vscode-border-secondary)]'
                  }`}
                >
                  {/* Top accent stripe */}
                  {plan.popular && (
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-[var(--vscode-primary)]" />
                  )}

                  {/* Card header */}
                  <div className="p-4 sm:p-5 pb-3">
                    {/* Name + badge */}
                    <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                      <h2 className="text-base sm:text-lg font-bold text-[var(--vscode-text-primary)]">{plan.name}</h2>
                      <div className="flex flex-col items-end gap-1">
                        {plan.badge && (
                          <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${plan.badge.color}`}>
                            {plan.badge.text}
                          </span>
                        )}
                        {plan.autoRenew && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400">
                            <RefreshCw className="w-2.5 h-2.5" />
                            {copy.autoRenew}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[var(--vscode-text-secondary)] mb-3 leading-snug">{plan.description}</p>

                    {/* Price block */}
                    <div className="mb-3">
                      {plan.priceLabel ? (
                        <div className="text-2xl sm:text-3xl font-bold text-[var(--vscode-text-primary)]">
                          {plan.priceLabel}
                        </div>
                      ) : (
                        <div>
                          <div className="flex flex-wrap items-baseline gap-1">
                            <span className="text-2xl sm:text-3xl font-bold text-[var(--vscode-text-primary)]">
                              {plan.packId ? formatPackPrice(plan.packId, locale) : ''}
                            </span>
                            <span className="text-xs text-[var(--vscode-text-tertiary)]">{plan.periodLabel}</span>
                          </div>
                          {plan.effectiveLabel && (
                            <p className="text-xs text-[var(--vscode-text-tertiary)] mt-0.5">≈ {plan.effectiveLabel} {copy.effectiveLabel}</p>
                          )}
                          {plan.savePct && (
                            <span className="inline-flex items-center gap-1 mt-1 text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                              <TrendingDown className="w-3 h-3" />
                              {plan.savePct} {copy.saveVsMonthly}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Optimizations callout */}
                      <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[var(--vscode-primary)]/10 border border-[var(--vscode-primary)]/20">
                        <Zap className="w-3 h-3 text-[var(--vscode-primary)] shrink-0" />
                        <span className="text-xs font-semibold text-[var(--vscode-primary)]">{plan.totalCredits}</span>
                      </div>
                      {plan.id === 'free' && (
                        <p className="text-xs text-[var(--vscode-text-tertiary)] mt-1">{copy.noCreditCard}</p>
                      )}
                    </div>
                  </div>

                  <div className="h-px bg-[var(--vscode-border-primary)] mx-4" />

                  {/* Feature list */}
                  <div className="flex-1 px-4 pt-3 pb-2">
                    <p className="text-xs uppercase tracking-wider text-[var(--vscode-text-tertiary)] font-medium mb-2">
                      {copy.whatsIncluded}
                    </p>
                    <ul className="space-y-2">
                      {featureRow.map(({ label, value }, idx) => (
                        <li key={idx} className="flex items-center justify-between gap-2 min-w-0">
                          <span className={`text-sm min-w-0 truncate ${
                            value === false || value === null
                              ? 'text-gray-400 dark:text-gray-600'
                              : 'text-[var(--vscode-text-secondary)]'
                          }`}>
                            {label}
                          </span>
                          <span className="flex-shrink-0 flex items-center justify-end">
                            <FeatureCell value={value} highlight={plan.popular} />
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <div className="p-4 pt-3">
                    <button
                      type="button"
                      onClick={() => handlePlanClick(plan)}
                      disabled={loading === plan.id}
                      className={`w-full min-h-[44px] py-2.5 px-4 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation ${
                        plan.popular
                          ? 'bg-[var(--brand-cta-bg)] text-white hover:opacity-90'
                          : plan.id === 'free'
                            ? 'bg-[var(--vscode-bg-tertiary)] text-[var(--vscode-text-primary)] hover:bg-[var(--vscode-bg-hover)] border border-[var(--vscode-border-primary)]'
                            : 'bg-[var(--brand-cta-bg)] text-white hover:opacity-90'
                      }`}
                    >
                      {loading === plan.id ? copy.loading : plan.buttonText}
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </div>

          {/* ── Feature Comparison Table (Our plans) ──────────────────── */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-16 sm:mb-20"
          >
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {copy.comparePlansTitle}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                {copy.comparePlansSubtitle}
              </p>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-[var(--vscode-border-primary)] shadow-sm">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-[var(--vscode-bg-elevated)] border-b border-[var(--vscode-border-primary)]">
                    <th className="text-left px-4 py-3 font-semibold text-[var(--vscode-text-secondary)] w-[36%]">{copy.featureColumn}</th>
                    <th className="px-3 py-3 font-semibold text-[var(--vscode-text-secondary)] text-center">{t('views.pricing.planFree')}</th>
                    <th className="px-3 py-3 font-semibold text-[var(--vscode-primary)] text-center bg-[var(--vscode-primary)]/5">
                      {t('views.pricing.planMonthly')}<br /><span className="text-xs font-normal text-[var(--vscode-text-tertiary)]">₹349</span>
                    </th>
                    <th className="px-3 py-3 font-semibold text-emerald-600 dark:text-emerald-400 text-center">
                      {t('views.pricing.planQuarterly')}<br /><span className="text-xs font-normal text-[var(--vscode-text-tertiary)]">₹749</span>
                    </th>
                    <th className="px-3 py-3 font-semibold text-amber-600 dark:text-amber-400 text-center">
                      {t('views.pricing.planHalfYearly')}<br /><span className="text-xs font-normal text-[var(--vscode-text-tertiary)]">₹1,499</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {planFeatures.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`border-b border-[var(--vscode-border-primary)] last:border-0 ${
                        idx % 2 === 0
                          ? 'bg-[var(--vscode-bg-primary)]'
                          : 'bg-[var(--vscode-bg-elevated)]'
                      }`}
                    >
                      <td className="px-4 py-3 text-[var(--vscode-text-secondary)] font-medium">{row.label}</td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex justify-center"><FeatureCell value={row.free} /></div>
                      </td>
                      <td className="px-3 py-3 text-center bg-[var(--vscode-primary)]/5">
                        <div className="flex justify-center"><FeatureCell value={row.monthly} highlight /></div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex justify-center"><FeatureCell value={row.quarterly} /></div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex justify-center"><FeatureCell value={row.halfyearly} /></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.section>

          {/* ── Competitor Comparison ─────────────────────────────────── */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-16 sm:mb-20"
          >
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {copy.competitorTitle}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {copy.competitorSubtitle}
              </p>
            </div>

            {/* Price highlight pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {competitors.slice(1).map((name, i) => (
                priceSavings[i + 1] ? (
                  <div key={name} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--vscode-bg-elevated)] border border-[var(--vscode-border-primary)] text-xs">
                    <span className="text-[var(--vscode-text-secondary)] font-medium">{name}</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold">{priceSavings[i + 1]}</span>
                  </div>
                ) : null
              ))}
            </div>

            <div className="overflow-x-auto rounded-2xl border border-[var(--vscode-border-primary)] shadow-sm">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="bg-[var(--vscode-bg-elevated)] border-b border-[var(--vscode-border-primary)]">
                    <th className="text-left px-4 py-3 font-semibold text-[var(--vscode-text-secondary)] w-[26%]">{copy.featureColumn}</th>
                    {competitors.map((name, i) => (
                      <th
                        key={name}
                        className={`px-3 py-3 font-bold text-center text-xs sm:text-sm ${
                          i === GGJ_COL
                            ? 'text-[var(--vscode-primary)] bg-[var(--vscode-primary)]/8'
                            : 'text-[var(--vscode-text-secondary)]'
                        }`}
                      >
                        {i === GGJ_COL ? (
                          <span className="flex flex-col items-center gap-0.5">
                            <Star className="w-3.5 h-3.5 fill-current" />
                            {name}
                          </span>
                        ) : name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`border-b border-[var(--vscode-border-primary)] last:border-0 ${
                        idx % 2 === 0
                          ? 'bg-[var(--vscode-bg-primary)]'
                          : 'bg-[var(--vscode-bg-elevated)]'
                      }`}
                    >
                      <td className="px-4 py-3 text-[var(--vscode-text-secondary)] font-medium">{row.label}</td>
                      {row.values.map((val, ci) => (
                        <td
                          key={ci}
                          className={`px-3 py-3 text-center ${ci === GGJ_COL ? 'bg-[var(--vscode-primary)]/5' : ''}`}
                        >
                          <div className="flex justify-center">
                            {idx === 0 ? (
                              <span className={`font-bold text-xs sm:text-sm ${ci === GGJ_COL ? 'text-[var(--vscode-primary)]' : 'text-[var(--vscode-text-secondary)]'}`}>
                                {val as string}
                              </span>
                            ) : (
                              <FeatureCell value={val} highlight={ci === GGJ_COL} />
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[var(--vscode-bg-elevated)] border-t-2 border-[var(--vscode-primary)]/30">
                    <td className="px-4 py-3 text-xs text-[var(--vscode-text-tertiary)] italic" colSpan={7}>
                      {copy.competitorFootnote}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Positioning statement */}
            <div className="mt-6 p-4 sm:p-6 rounded-2xl bg-[var(--vscode-primary)]/8 border border-[var(--vscode-primary)]/20 text-center">
              <Award className="w-6 h-6 text-[var(--vscode-primary)] mx-auto mb-2" />
              <p className="text-sm sm:text-base font-semibold text-[var(--vscode-text-primary)] mb-1">
                {copy.positioningTitle}
              </p>
              <p className="text-xs sm:text-sm text-[var(--vscode-text-secondary)]">
                {copy.positioningBody}
              </p>
            </div>
          </motion.section>

          {/* ── Footer note ────────────────────────────────────────────── */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="text-center text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-4 px-2"
          >
            {copy.footerInr}{' '}
            <button
              type="button"
              onClick={() => onNavigate?.('contact-us')}
              className="font-medium text-[var(--vscode-text-link)] hover:underline touch-manipulation min-h-[44px] inline-flex items-center"
            >
              {copy.contactUs}
            </button>{' '}
            {copy.forCustomNeeds}
          </motion.p>
        </div>
      </main>

      <PricingFAQ onNavigate={onNavigate} />
      </div>

      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <MarketingPageFooter onNavigate={onNavigate as any} theme="pricing" />

      {Capacitor.isNativePlatform() && (
        <CreditPurchaseModal
          isOpen={purchaseModalOpen}
          initialPackId={checkoutPackId}
          onClose={() => setPurchaseModalOpen(false)}
          onPurchaseComplete={() => {
            setPurchaseModalOpen(false);
            if (onNavigate) onNavigate('dashboard');
          }}
        />
      )}
    </div>
  );
};

export default PricingPage;
